import type { Melody } from '@tunasong/schemas'
import type { FC } from 'react'
import { MelodyMiniRoll } from '../melody-roll/melody-mini-roll.js'
import { PitchInputMeter, type PitchMeterState } from '../pitch-training/pitch-input-meter.js'
import type { AttemptPhase } from './trainer-attempt.js'
import styles from './melody-trainer-question.module.css'

/**
 * What the player looks at during an attempt.
 *
 * The melody stays on screen the whole time, which is the deliberate difference from the Pitch
 * Trainer: that tool tests whether you can hold a pitch you heard, and showing it would answer the
 * question for you. This one tests whether you can REPRODUCE a line - notes and placement - and a
 * musician reproducing a line has the music in front of them.
 *
 * The phase line names what is happening, because the attempt is a sequence a player has to follow
 * without being told twice: knocks, melody, one beat, go.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6.4
 */

const PHASE_TEXT: Record<AttemptPhase, string> = {
  'lead-in': 'Listening to the room — stay quiet for a moment.',
  'count-in': 'Two knocks, then the melody.',
  reference: 'Listen.',
  gap: 'One beat…',
  take: 'Your turn — play it with the click.',
  scoring: 'Working out what you played...',
}

export interface MelodyTrainerQuestionProps {
  melody: Melody
  index: number
  total: number
  /** Null between attempts, when nothing is running. */
  phase: AttemptPhase | null
  playheadMs: number | null
  /** Live input, in audio modes. Absent for MIDI, which opens no microphone. */
  meter?: PitchMeterState
}

export const MelodyTrainerQuestion: FC<MelodyTrainerQuestionProps> = props => {
  const { melody, index, total, phase, playheadMs, meter } = props

  return (
    <section className={styles.question} aria-label="Attempt">
      <p className={styles.counter}>{`Attempt ${index + 1} of ${total}`}</p>

      <MelodyMiniRoll melody={melody} playheadMs={playheadMs} className={styles.roll} />

      <div className={styles.phase} role="status">
        {phase ? PHASE_TEXT[phase] : ''}
      </div>

      {meter ? (
        <PitchInputMeter frame={meter.frame} noiseFloorRms={meter.noiseFloorRms} deviceLabel={meter.deviceLabel} />
      ) : null}
    </section>
  )
}
